import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { FaTrash } from "react-icons/fa";

const ListadoReservas = () => {
  const [reservas, setReservas] = useState([]);
  const [filteredReservas, setFilteredReservas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtros, setFiltros] = useState({
    cliente_nombre: "",
    cliente_apellido: "",
    apartamento_nombre: "",
    descripcion: "",
  });
  const [estadoFiltro, setEstadoFiltro] = useState("todas");
  const [ordenFechaAscendente, setOrdenFechaAscendente] = useState(true);

  useEffect(() => {
    const fetchReservas = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/reservas`);
        setReservas(response.data);
        setFilteredReservas(response.data);
      } catch (err) {
        setError("Error al cargar las reservas");
      } finally {
        setLoading(false);
      }
    };
    fetchReservas();
  }, []);

  const sanitizeInput = (input) => {
    const tempElement = document.createElement("div");
    tempElement.textContent = input;
    return tempElement.innerHTML;
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const sanitizedValue = sanitizeInput(value);
    setFiltros((prevFiltros) => ({
      ...prevFiltros,
      [name]: sanitizedValue,
    }));
    filtrarReservas({ ...filtros, [name]: sanitizedValue }, estadoFiltro);
  };

  const handleEstadoFiltroChange = (e) => {
    const nuevoEstadoFiltro = e.target.value;
    setEstadoFiltro(nuevoEstadoFiltro);
    filtrarReservas(filtros, nuevoEstadoFiltro);
  };

  const filtrarReservas = (filtros, estado) => {
    const fechaActual = new Date();
    const reservasFiltradas = reservas.filter((reserva) => {
      const cumpleFiltroEstado =
        estado === "todas" ||
        (estado === "activas" && new Date(reserva.fecha_fin) >= fechaActual) ||
        (estado === "inactivas" && new Date(reserva.fecha_fin) < fechaActual);

      const cumpleFiltrosTexto = Object.keys(filtros).every((key) =>
        (reserva[key] ? reserva[key].toString().toLowerCase() : "").includes(
          filtros[key].toLowerCase()
        )
      );

      return cumpleFiltroEstado && cumpleFiltrosTexto;
    });

    setFilteredReservas(reservasFiltradas);
  };

  const ordenarPorFechaInicio = () => {
    const reservasOrdenadas = [...filteredReservas].sort((a, b) => {
      const fechaInicioA = new Date(a.fecha_inicio);
      const fechaInicioB = new Date(b.fecha_inicio);

      if (ordenFechaAscendente) {
        return fechaInicioA - fechaInicioB;
      } else {
        return fechaInicioB - fechaInicioA;
      }
    });

    setFilteredReservas(reservasOrdenadas);
    setOrdenFechaAscendente(!ordenFechaAscendente);
  };

  const eliminarReserva = async (id) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: "No podrás revertir esta acción.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL}/reservas/${id}`);
        setReservas(reservas.filter((reserva) => reserva.id !== id));
        setFilteredReservas(
          filteredReservas.filter((reserva) => reserva.id !== id)
        );
        Swal.fire(
          "Eliminado",
          "La reserva ha sido eliminada con éxito.",
          "success"
        );
      } catch (error) {
        Swal.fire("Error", "No se pudo eliminar la reserva", "error");
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-gray-600 text-lg animate-pulse">
          Cargando reservas...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-red-600 text-lg">{error}</p>
      </div>
    );
  }

  if (reservas.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-gray-600 text-lg">No hay reservas registradas.</p>
      </div>
    );
  }

  return (
    <section className="min-h-screen flex flex-col items-center p-8 bg-gray-100">
      <div className="w-full max-w-6xl p-6 border rounded-lg shadow-lg bg-white">
        <h2 className="text-3xl font-extrabold text-center mb-6 tracking-wide text-gray-800">
          Listado de Reservas
        </h2>

        {/* Filtros para buscar reservas */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          {Object.keys(filtros).map((key) => (
            <input
              key={key}
              type="text"
              name={key}
              placeholder={`Filtrar por ${
                key.charAt(0).toUpperCase() + key.slice(1)
              }`}
              value={filtros[key]}
              onChange={handleFilterChange}
              className="border rounded-md p-2 focus:outline-none focus:ring focus:border-blue-300"
            />
          ))}
          <select
            value={estadoFiltro}
            onChange={handleEstadoFiltroChange}
            className="border rounded-md p-2 focus:outline-none focus:ring focus:border-blue-300"
          >
            <option value="todas">Todas</option>
            <option value="activas">Activas</option>
            <option value="inactivas">Inactivas</option>
          </select>
        </div>

        {/* Tabla de Reservas */}
        <div className="overflow-x-auto">
          <table className="table-auto w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-200">
                <th className="border px-4 py-2">Nombre del Cliente</th>
                <th
                  className="border px-4 py-2 cursor-pointer"
                  onClick={ordenarPorFechaInicio}
                >
                  Fecha de Inicio {ordenFechaAscendente ? "↑" : "↓"}
                </th>

                <th className="border px-4 py-2">Fecha de Fin</th>
                <th className="border px-4 py-2">Nombre del Apartamento</th>
                <th className="border px-4 py-2">Descripción</th>
                <th className="border px-4 py-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredReservas.map((reserva, index) => (
                <tr
                  key={index}
                  className={`${
                    index % 2 === 0 ? "bg-gray-50" : "bg-white"
                  } hover:bg-gray-100`}
                >
                  <td className="border px-4 py-2">
                    {reserva.cliente_nombre} {reserva.cliente_apellido || ""}
                  </td>
                  <td className="border px-4 py-2">
                    {new Date(reserva.fecha_inicio).toLocaleDateString("es-ES")}
                  </td>
                  <td className="border px-4 py-2">
                    {new Date(reserva.fecha_fin).toLocaleDateString("es-ES")}
                  </td>

                  <td className="border px-4 py-2">
                    {reserva.apartamento_nombre}
                  </td>
                  <td className="border px-4 py-2">
                    <DescripcionExpandable descripcion={reserva.descripcion} />
                  </td>
                  <td className="border px-4 py-2">
                    <button
                      onClick={() => eliminarReserva(reserva.id)}
                      className="text-red-500 hover:text-red-700"
                      title="Eliminar"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

// Componente de Descripción Expandible
const DescripcionExpandable = ({ descripcion }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div>
      <p>
        {isExpanded
          ? descripcion
          : descripcion?.slice(0, 50) + (descripcion?.length > 50 ? "..." : "")}
      </p>
      {descripcion?.length > 50 && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-blue-500 hover:text-blue-700 focus:outline-none"
        >
          {isExpanded ? "Leer menos" : "Leer más"}
        </button>
      )}
    </div>
  );
};

export default ListadoReservas;
